import Microgame from '../engine/Microgame.js';

export default class FlySwatGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Mouche !`, en: `Swat!`, es: `¡Mosca!` },
            controls: { fr: `Appuie exactement sur la mouche pour l'écraser`, en: `Tap exactly on the fly to swat it`, es: `Toca exactamente en la mosca para aplastarla` },
            objective: { fr: `Écrase la mouche insaisissable avant qu'elle ne disparaisse`, en: `Swat the elusive fly before it escapes`, es: `Aplastad la mosca escurridiza antes de que se escape` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M12 2v20M2 12h20M7 7l10 10M17 7 7 17"/></svg>`,
            actions: [
                { type: 'wait', duration: 420 },
                { type: 'tapTarget', selector: '[data-playback-target="fly"]', duration: 160 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.fly = null;
        this.swatter = null;
        this.splatter = null;

        // Target pos
        this.targetX = 50;
        this.targetY = 50;

        this.moveTimer = 0;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Duration: tighter with speed. Default game is normally 3000-4000.
        const adjustedSpeed = 1 + (speedMult - 1) * 0.5;
        this.duration = 3000 * (speedMult / adjustedSpeed);

        // Fly movement speed scaling: heavily nerfed, making max difficulty equivalent to the old easiest difficulty
        this.tickRate = Math.max(350, 800 - (speedMult * 100)); // Move every 0.8s to 0.4s
    }

    setup() {
        this.engine.ui.instruction.textContent = "SWAT!";

        // Splatter (hidden initially)
        this.splatter = document.createElement('div');
        this.splatter.style.position = 'absolute';
        this.splatter.style.width = '120px';
        this.splatter.style.height = '120px';
        this.splatter.style.background = `radial-gradient(circle, ${this.primaryColor} 0%, transparent 60%)`;
        this.splatter.style.borderRadius = '50%';
        this.splatter.style.opacity = '0'; // Hidden
        this.splatter.style.transform = 'translate(-50%, -50%) scale(0)';
        this.container.appendChild(this.splatter);

        // Fly Container
        this.fly = document.createElement('div');
        this.fly.style.position = 'absolute';
        this.fly.dataset.playbackTarget = 'fly';
        this.fly.style.width = '24px';
        this.fly.style.height = '30px';
        this.fly.style.transition = 'left 0.15s ease-out, top 0.15s ease-out, transform 0.1s';
        this.fly.style.zIndex = '5';
        this.container.appendChild(this.fly);

        // Fly Body
        this.body = document.createElement('div');
        this.body.style.position = 'absolute';
        this.body.style.width = '20px';
        this.body.style.height = '28px';
        this.body.style.background = this.secondaryColor;
        this.body.style.borderRadius = '10px 10px 15px 15px';
        this.body.style.left = '50%';
        this.body.style.top = '50%';
        this.body.style.transform = 'translate(-50%, -50%)';
        this.body.style.boxShadow = `0 4px 10px rgba(0,0,0,0.5)`;
        this.body.style.transition = 'all 0.1s ease';
        this.fly.appendChild(this.body);

        // Fly Eyes
        const eyeL = document.createElement('div');
        eyeL.style.position = 'absolute';
        eyeL.style.width = '8px';
        eyeL.style.height = '8px';
        eyeL.style.background = this.primaryColor;
        eyeL.style.borderRadius = '50%';
        eyeL.style.top = '2px';
        eyeL.style.left = '1px';
        this.body.appendChild(eyeL);

        const eyeR = document.createElement('div');
        eyeR.style.position = 'absolute';
        eyeR.style.width = '8px';
        eyeR.style.height = '8px';
        eyeR.style.background = this.primaryColor;
        eyeR.style.borderRadius = '50%';
        eyeR.style.top = '2px';
        eyeR.style.right = '1px';
        this.body.appendChild(eyeR);

        // Wings
        this.wingL = document.createElement('div');
        this.wingL.style.position = 'absolute';
        this.wingL.style.width = '20px';
        this.wingL.style.height = '12px';
        this.wingL.style.background = 'rgba(255,255,255,0.7)';
        this.wingL.style.border = `1px solid ${this.primaryColor}`;
        this.wingL.style.borderRadius = '50%';
        this.wingL.style.left = '-12px';
        this.wingL.style.top = '8px';
        this.wingL.style.transformOrigin = 'right center';
        this.wingL.style.animation = 'flutterL 0.05s infinite alternate';
        this.fly.appendChild(this.wingL);

        this.wingR = document.createElement('div');
        this.wingR.style.position = 'absolute';
        this.wingR.style.width = '20px';
        this.wingR.style.height = '12px';
        this.wingR.style.background = 'rgba(255,255,255,0.7)';
        this.wingR.style.border = `1px solid ${this.primaryColor}`;
        this.wingR.style.borderRadius = '50%';
        this.wingR.style.right = '-12px';
        this.wingR.style.top = '8px';
        this.wingR.style.transformOrigin = 'left center';
        this.wingR.style.animation = 'flutterR 0.05s infinite alternate';
        this.fly.appendChild(this.wingR);

        // Define Wing Animations globally
        let styleEl = document.getElementById('fly-game-styles');
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = 'fly-game-styles';
            styleEl.innerHTML = `
                @keyframes flutterL { from { transform: rotate(0deg) scaleY(1); } to { transform: rotate(-45deg) scaleY(0.5); } }
                @keyframes flutterR { from { transform: rotate(0deg) scaleY(1); } to { transform: rotate(45deg) scaleY(0.5); } }
            `;
            document.head.appendChild(styleEl);
        }

        // Initialize Fly Position
        this.fly.style.left = '50%';
        this.fly.style.top = '50%';
        this.fly.style.transform = 'translate(-50%, -50%)';

        // Swatter (Visual)
        this.swatter = document.createElement('div');
        this.swatter.style.position = 'absolute';
        this.swatter.style.width = '120px'; // Hit area size essentially
        this.swatter.style.height = '120px';
        this.swatter.style.display = 'none';
        this.swatter.style.pointerEvents = 'none';
        this.swatter.style.zIndex = '100';
        this.container.appendChild(this.swatter);

        // Swatter Handle
        const handle = document.createElement('div');
        handle.style.position = 'absolute';
        handle.style.width = '12px';
        handle.style.height = '120px';
        handle.style.background = 'linear-gradient(to right, #666, #444)';
        handle.style.bottom = '-40px';
        handle.style.left = '50%';
        handle.style.transform = 'translateX(-50%) rotate(15deg)';
        handle.style.transformOrigin = 'bottom center';
        handle.style.borderRadius = '6px';
        this.swatter.appendChild(handle);

        // Swatter Head (Grid)
        const head = document.createElement('div');
        head.style.position = 'absolute';
        head.style.width = '90px';
        head.style.height = '100px';
        head.style.border = `6px solid ${this.primaryColor}`;
        head.style.background = `repeating-linear-gradient(0deg, transparent, transparent 6px, rgba(255,255,255,0.4) 6px, rgba(255,255,255,0.4) 8px),
                                 repeating-linear-gradient(90deg, transparent, transparent 6px, rgba(255,255,255,0.4) 6px, rgba(255,255,255,0.4) 8px)`;
        head.style.backgroundColor = `rgba(0,0,0,0.6)`;
        head.style.borderRadius = '35px 35px 20px 20px';
        head.style.top = '10px';
        head.style.left = '50%';
        head.style.transform = 'translateX(-50%)';
        head.style.boxShadow = '0 15px 30px rgba(0,0,0,0.5)';
        this.swatter.appendChild(head);

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    update(dt) {
        if (!this.active || this.won || this.isDead) return;

        this.moveTimer -= dt;
        if (this.moveTimer <= 0) {
            this.moveTimer = this.tickRate;

            this.targetX = 15 + Math.random() * 70;
            this.targetY = 15 + Math.random() * 70;

            const dx = this.targetX - parseFloat(this.fly.style.left || 50);
            const dy = this.targetY - parseFloat(this.fly.style.top || 50);
            let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;

            this.fly.style.left = `${this.targetX}%`;
            this.fly.style.top = `${this.targetY}%`;
            this.fly.style.transform = `translate(-50%, -50%) rotate(${angle}deg)`;
        }
    }

    onDown(e) {
        if (!this.active || this.won || this.isDead) return;

        const rect = this.container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Show swatter hit animation
        this.swatter.style.left = `${x}px`;
        this.swatter.style.top = `${y}px`;
        this.swatter.style.transform = 'translate(-50%, -50%) scale(1.6)';
        this.swatter.style.opacity = '0.4';
        this.swatter.style.display = 'block';

        setTimeout(() => {
            if (!this.swatter) return;
            this.swatter.style.transition = 'all 0.05s ease-out';
            this.swatter.style.transform = 'translate(-50%, -50%) scale(1)';
            this.swatter.style.opacity = '1';
        }, 10);

        // Smash dust
        const smash = document.createElement('div');
        smash.style.position = 'absolute';
        smash.style.left = `${x}px`;
        smash.style.top = `${y}px`;
        smash.style.width = '100px';
        smash.style.height = '100px';
        smash.style.background = 'rgba(255,255,255,0.5)';
        smash.style.borderRadius = '50%';
        smash.style.transform = 'translate(-50%, -50%) scale(0)';
        smash.style.transition = 'all 0.15s ease-out';
        smash.style.pointerEvents = 'none';
        this.container.appendChild(smash);

        setTimeout(() => {
            smash.style.transform = 'translate(-50%, -50%) scale(1)';
            smash.style.opacity = '0';
        }, 10);
        setTimeout(() => smash.remove(), 200);

        // Check distance to fly center
        const fRect = this.fly.getBoundingClientRect();
        const fx = fRect.left + fRect.width / 2;
        const fy = fRect.top + fRect.height / 2;

        const dist = Math.sqrt((x - (fx - rect.left)) ** 2 + (y - (fy - rect.top)) ** 2);

        // Hit logic
        if (dist < 75) { // Large hit radius for fairness
            this.isDead = true;
            this.engine.timer += 2000; // Artificially extend timer so it doesn't fail while we admire the splatter

            // Crush Fly
            this.fly.style.transition = 'none'; // Stop it sliding instantly if it just moved
            this.wingL.style.animation = 'none';
            this.wingL.style.opacity = '0';

            this.wingR.style.animation = 'none';
            this.wingR.style.opacity = '0';

            this.body.style.transform = 'translate(-50%, -50%) scale(1.8, 0.4)'; // SQUASH
            this.body.style.background = this.primaryColor;
            this.body.style.boxShadow = 'none';

            // Blood Splatter
            this.splatter.style.left = `${fx - rect.left}px`;
            this.splatter.style.top = `${fy - rect.top}px`;
            this.splatter.style.transition = 'all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
            this.splatter.style.opacity = '1';
            this.splatter.style.transform = 'translate(-50%, -50%) scale(1)';

            // Trigger actual win after delay
            setTimeout(() => {
                if (this.active) this.win();
            }, 800);

        } else {
            // Miss - hide swatter after a bit
            setTimeout(() => {
                if (!this.won && this.swatter) this.swatter.style.display = 'none';
            }, 120);
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
